import { Box, Button, CircularProgress } from '@mui/material';
import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import LoadingSpinner from 'components/loading-spinner';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { TimerReset } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { putSimpleCalibrationInputs } from 'views/bluviu-nutrition/features/calibrations/api/simple-calibration';
import { useCalibrationState } from 'views/bluviu-nutrition/features/calibrations/context/calibration-context';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { SimpleCalibration } from 'views/bluviu-nutrition/features/calibrations/types/simple-calibration.type';
import { UpdateSimpleCalibrationInputsRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-simple-calibration-inputs-request.type';

const PAGE_NAME_ID = `bluViuNutrition.calibrations.editCalibration.inputs.single`;

const SimpleCalibrationInputs = () => {
  const intl = useIntl();
  const params = useParams<CalibrationsParams>();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    simpleCalibrationQuery: { data, isLoading },
  } = useCalibrationState();

  const defaultValues: UpdateSimpleCalibrationInputsRequest = useMemo(() => {
    if (!data)
      return {
        id: '',
        stockingDate: '',
        harvestDate: '',
        stockingFishWeight: '',
        harvestFishWeight: '',
        harvestCyclebFcr: '',
      };

    return {
      id: '',
      stockingDate: data.stockingDate,
      harvestDate: data.harvestDate,
      stockingFishWeight: String(data.stockingFishWeight),
      harvestFishWeight: String(data.harvestFishWeight),
      harvestCyclebFcr: String(data.harvestCyclebFcr),
    };
  }, [data]);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<UpdateSimpleCalibrationInputsRequest>({
    defaultValues: defaultValues,
  });

  useEffect(() => {
    reset(defaultValues); // form is not properly defaulted unless reset is called
  }, [data]);

  const updateCalibrationInputs = usePost<SimpleCalibration, UpdateSimpleCalibrationInputsRequest>(
    (request) => putSimpleCalibrationInputs(params.globalSiteId, params.calibrationId, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Inputs saved!' },
      });

      queryClient.invalidateQueries({
        queryKey: ['getSimpleCalibration', params.globalSiteId, params.calibrationId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleSaveCalibration = (form: UpdateSimpleCalibrationInputsRequest) => {
    if (!data) return;

    updateCalibrationInputs.mutate({
      stockingFishWeight: form.stockingFishWeight,
      stockingDate: form.stockingDate,
      harvestFishWeight: form.harvestFishWeight,
      harvestDate: form.harvestDate,
      harvestCyclebFcr: form.harvestCyclebFcr,
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (!data) return;

  return (
    <Collapsible label={'Inputs'} open>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <form onSubmit={handleSubmit(handleSaveCalibration)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              <Box display={'flex'} flexDirection={'row'} gap={4}>
                <DatePicker
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.stockingDate` })}
                  controller={{
                    control,
                    name: 'stockingDate',
                  }}
                />
                <DatePicker
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.harvestDate` })}
                  controller={{
                    control,
                    name: 'harvestDate',
                  }}
                />
              </Box>
              <Box display={'flex'} flexDirection={'row'} gap={4}>
                <TextField
                  dark
                  fullWidth
                  type="number"
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.stockingFishWeight` })}
                  controller={{
                    control,
                    name: 'stockingFishWeight',
                    rules: {
                      validate: {
                        greaterThan: (value) => (value >= 50 ? true : 'Value must be greater than 50'),
                      },
                    },
                  }}
                  endAdornment={'g'}
                />
                <TextField
                  dark
                  fullWidth
                  type="number"
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.harvestFishWeight` })}
                  controller={{
                    control,
                    name: 'harvestFishWeight',
                    rules: {
                      validate: {
                        greaterThan: (value) => {
                          const stockingWeight = getValues('stockingFishWeight');
                          if (Number(value) > Number(stockingWeight)) return true;
                          else return `Value must be greater than ${stockingWeight}`;
                        },
                      },
                    },
                  }}
                  endAdornment={'g'}
                />
              </Box>
              <TextField
                dark
                fullWidth
                type="number"
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.harvestCyclebFcr` })}
                controller={{
                  control,
                  name: 'harvestCyclebFcr',
                  rules: {
                    validate: {
                      between: (v) =>
                        parseFloat(v) >= 0.8 && parseFloat(v) <= 2.5
                          ? true
                          : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0.8', upper: '2.5' }),
                    },
                  },
                }}
              />
            </Box>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'}>
                <Mui.Tooltip title={'Reset inputs'}>
                  <Mui.IconButton disabled={!isDirty} onClick={() => reset(defaultValues)}>
                    <TimerReset size={22} />
                  </Mui.IconButton>
                </Mui.Tooltip>
              </Box>
              <Button
                disabled={updateCalibrationInputs.isPending || !isDirty}
                startIcon={updateCalibrationInputs.isPending && <CircularProgress color="inherit" size={18} />}
                type="submit">
                <FormattedMessage id={`general.save`} />
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Collapsible>
  );
};

export default React.memo(SimpleCalibrationInputs);
